import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { Alert, Badge, ListGroup } from "react-bootstrap";
import type {
  BillingFinalizeAttempt,
  BillingFinalizeOutcome,
} from "../../../api/src/billing/billing-finalize-workflow-state";
import type { BillingController } from "../../../api/src/billing/billing.controller";
import type { SerializedObject, UnpackResponse } from "../../../api/src/lib";
import type { UsersController } from "../../../api/src/users/users.controller";
import Loading from "../Loading";
import FormError from "../components/FormError";
import SubmitButton from "../components/SubmitButton";
import {
  deserializeDate,
  useAuthenticatedFetch,
  useAuthenticatedMutationNew,
} from "../lib/api";

/** `GET /v1/billing/reports/:id/finalize-workflow`, as it comes off the wire. */
type WorkflowBody = SerializedObject<
  UnpackResponse<BillingController["getBillingReportFinalizeWorkflow"]>
>;

type SerializedStepState = WorkflowBody["data"]["steps"][number];
type SerializedAttempt = SerializedObject<BillingFinalizeAttempt>;

/** `GET /users` — the firm's Auth0 users, as `UsersManager` reads them too. */
type UsersBody = UnpackResponse<UsersController["getAll"]>;

/**
 * What to call each Auth0 subject `billing_report_workflow.created_by` stores,
 * keyed by the subject itself.
 *
 * A MAP AND NOT A REWRITE OF THE STORED VALUE (APP-1474). The subject is the
 * audit identity: it does not change when a user is renamed and it survives that
 * user leaving the firm, which is why the column keeps it. This is the display
 * layer over it, and nothing here writes back.
 */
type UserNames = ReadonlyMap<string, string>;

/**
 * The name to show for the user who made an attempt (R10).
 *
 * FALLS BACK TO THE SUBJECT, WHICH IS THE POINT OF THE FALLBACK. `GET /users`
 * lists the firm's current users; a user who finalized a report and has since
 * been removed from the firm is not among them, and neither is anyone if the
 * users read failed or has not landed yet. The attempt line still has to render
 * — an audit log that blanks its actor is worse than one that names them
 * awkwardly.
 */
function displayName(names: UserNames, createdBy: string) {
  return names.get(createdBy) ?? createdBy;
}

/**
 * How often the panel re-reads the workflow while a step is still with the
 * worker.
 *
 * Tens of seconds, deliberately — but not because the read is expensive. The
 * route scopes itself with `assertBillingReportIsThisFirms`, one indexed
 * lookup, precisely so this poll is cheap (`billing.controller.ts`). The
 * interval is set by what it is watching: a publish step paces itself against
 * Slant and drains for about 82 minutes
 * (s-billing-report-finalization-capability-019fba175c3d304d, §Pacing), so half
 * a minute of staleness costs the advisor nothing on that timescale.
 */
const REFETCH_INTERVAL_MS = 30_000;

/**
 * How long a `running` attempt may go without a heartbeat before the re-run
 * control is offered against it.
 *
 * Five times `BILLING_FINALIZE_HEARTBEAT_MS`, so four consecutive missed beats
 * still read as alive: a heartbeat is one indexed UPDATE and losing a few to a
 * slow database or a busy worker must not put a Re-run button beside a step that
 * is publishing correctly. The cost of the wait is bounded and one-directional —
 * a genuinely dead worker is recoverable a few minutes later rather than
 * immediately, against a publish that legitimately runs for hours.
 */
const RUNNING_ATTEMPT_STALE_AFTER_MS = 5 * 60_000;

/**
 * Whether the workflow can still change with nobody touching this page.
 *
 * ONE STATE STOPS THE POLL: `stepToRerun === null`, every step succeeded, which
 * is terminal by R29 — there is no re-run after success. Every other state can
 * move without this page doing anything, INCLUDING `failed` and `abandoned`.
 * Treating those two as halted is what froze the panel: the poll stopped, and
 * the page went on offering Re-run against data it would never refresh, for the
 * whole 82 minutes a publish takes. Any firm user can press Finalize or Re-run
 * (R4), so a second person's press moves a step this page believes is parked —
 * and a press made against a frozen `failed` reading lands on an attempt that
 * has since gone `running`, abandoning a live step's outcome.
 *
 * The cost of being wrong the other way is two indexed lookups a minute
 * (see {@link REFETCH_INTERVAL_MS}), which is why this errs towards polling.
 */
function isWithTheWorker(body: WorkflowBody | undefined) {
  if (typeof body === "undefined") return false;

  return body.data.stepToRerun !== null;
}

/**
 * The words the advisor reads for an outcome. `null` is not a fifth outcome —
 * a step with no attempt row has never been attempted (spec §The finalize
 * workflow).
 */
function outcomeLabel(outcome: BillingFinalizeOutcome | null) {
  switch (outcome) {
    case "running":
      return "Running";
    case "succeeded":
      return "Succeeded";
    case "failed":
      return "Failed";
    case "abandoned":
      return "Abandoned";
    default:
      return "Not attempted";
  }
}

function outcomeVariant(outcome: BillingFinalizeOutcome | null) {
  switch (outcome) {
    case "running":
      return "info";
    case "succeeded":
      return "success";
    case "failed":
      return "danger";
    case "abandoned":
      return "warning";
    default:
      return "secondary";
  }
}

/**
 * A coarse duration, for a step whose timescale runs from seconds to hours: a
 * publish paces itself against Slant and drains for about 82 minutes on a large
 * firm. Seconds below a minute, then minutes, then hours and minutes — never
 * more precision than the 30s poll behind it can honour.
 *
 * Exported for its own tests, which is where the three boundaries are pinned.
 */
export function formatDuration(ms: number): string {
  const seconds = Math.max(0, Math.round(ms / 1000));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

/**
 * How long a running attempt has been going, and how long since it last said so.
 *
 * THE TWO QUESTIONS AN ADVISOR HAS DURING THE HOUR A LARGE PUBLISH TAKES that
 * the household counts beside them do not answer: is it still going, and how
 * long has it been going. {@link StepCounts} says how far it has got; these two
 * say whether it is still getting anywhere.
 *
 * `sinceBeat` reads the same `updated_time` heartbeat the re-run gate reads, so
 * a number climbing past {@link RUNNING_ATTEMPT_STALE_AFTER_MS} is the advisor
 * watching the same evidence that will shortly offer them Re-run.
 *
 * Both refresh at the poll, not on a timer of their own: a second clock ticking
 * against data half a minute old would show a precision the panel does not have.
 */
const RunningFor = ({ attempt }: { attempt: SerializedAttempt }) => {
  const now = Date.now();
  const elapsed = now - deserializeDate(attempt.createdTime).getTime();
  const sinceBeat = now - deserializeDate(attempt.updatedTime).getTime();

  return (
    <>
      {` · running ${formatDuration(elapsed)}`}
      {` · last activity ${formatDuration(sinceBeat)} ago`}
    </>
  );
};

/**
 * How far a step has got, in households (APP-1475).
 *
 * WHAT THE OUTCOME BADGE CANNOT SAY. `Succeeded` reads as "all done" on a report
 * where 2 of 36 households reached Slant and 34 were never mapped, and `Running`
 * says nothing at all for the 82 minutes a firm-wide publish drains for. These
 * are the numbers the step's own summary log has always carried
 * (`api/src/billing/billing-finalize-publish.ts`), put where the advisor is
 * already looking.
 *
 * THE LAST COUNT IS CALLED `failed` ONLY ONCE THE ATTEMPT HAS ENDED. The api
 * returns it as `remaining`, because while the step is running the households it
 * has not reached and the households it failed on are the same rows — both are
 * households whose `published_time` has not moved, and the database cannot tell
 * them apart. Once the attempt is over there is nothing left to reach, so what
 * remains is what did not publish.
 *
 * `cleared` appears only when there is one. It is a household whose figures were
 * taken back OFF a Slant record because its mapping had gone (R33) — a real
 * outcome, and a rare one, and a "0 cleared" on every report would be noise
 * standing in front of the numbers that matter.
 */
const StepCounts = ({
  counts,
  outcome,
}: {
  counts: NonNullable<SerializedStepState["counts"]>;
  outcome: BillingFinalizeOutcome | null;
}) => (
  <div className="small text-secondary mt-1">
    {`${counts.published} of ${counts.households} published`}
    {counts.cleared <= 0 ? null : ` · ${counts.cleared} cleared`}
    {` · ${counts.unmapped} unmapped`}
    {outcome === "running" ? null : ` · ${counts.remaining} failed`}
  </div>
);

/**
 * One attempt: how it ended, who made it (R10), and when they made it.
 *
 * WHAT HALTED A FAILED STEP IS NOT HERE (APP-1473). The api does not return it,
 * because it is whatever the upstream service said and this route is open to
 * every user of the firm; the full text is on the attempt row, and the support
 * request that needs it is the attempt id
 * (`api/src/billing/billing-finalize-workflow-state.ts`).
 *
 * A `running` attempt shows no end time, because it has none. `updated_time` on
 * a row still reading `running` is its LAST HEARTBEAT, which the runner stamps
 * every `BILLING_FINALIZE_HEARTBEAT_MS` for as long as the step is in flight
 * (`api/src/billing/billing-finalize-runner.ts`) — it drifts forward every minute
 * and is not when the attempt started. That is `created_time`, which is what the
 * line reads before the dot. Showing the heartbeat as "ended" would put a moving
 * end time on a step that has not ended. It reads as elapsed time instead
 * ({@link RunningFor}): both numbers were already on the row and neither was
 * shown, which left the whole of an 82-minute publish reading as one word.
 */
const WorkflowAttempt = ({
  attempt,
  names,
}: {
  attempt: SerializedAttempt;
  names: UserNames;
}) => (
  <li className="mb-1">
    <span className="fw-semibold">{outcomeLabel(attempt.outcome)}</span>
    {" · "}
    {displayName(names, attempt.createdBy)}
    {" · "}
    {deserializeDate(attempt.createdTime).toLocaleString()}
    {attempt.outcome === "running" ? (
      <RunningFor attempt={attempt} />
    ) : (
      <>
        {" · ended "}
        {deserializeDate(attempt.updatedTime).toLocaleString()}
      </>
    )}
  </li>
);

/**
 * One step: its outcome, and every attempt at it oldest first (R28).
 *
 * Every attempt, not just the latest and not just the failures: a step that
 * failed and was re-run to success keeps both rows, and which attempt failed,
 * who made it and when are what the advisor is reading the panel to find (AC23b,
 * AC25).
 *
 * A step that reports how far it has got shows that under its label
 * ({@link StepCounts}); one that does not carries `counts: null` and this renders
 * nothing extra, which is how a third step added later needs no change here. The
 * counts are withheld until the step has been attempted at all: "0 of 36
 * published" against a step nobody has started reads as a failure.
 */
const WorkflowStep = ({
  step,
  names,
}: {
  step: SerializedStepState;
  names: UserNames;
}) => (
  <ListGroup.Item as="li">
    <div className="d-flex justify-content-between align-items-center">
      <span>{step.label}</span>
      <Badge bg={outcomeVariant(step.outcome)}>
        {outcomeLabel(step.outcome)}
      </Badge>
    </div>
    {step.counts == null || step.outcome === null ? null : (
      <StepCounts counts={step.counts} outcome={step.outcome} />
    )}
    {step.attempts.length <= 0 ? null : (
      <ul className="list-unstyled small text-secondary mt-2 mb-0">
        {step.attempts.map((attempt) => (
          <WorkflowAttempt
            key={attempt.attemptId}
            attempt={attempt}
            names={names}
          />
        ))}
      </ul>
    )}
  </ListGroup.Item>
);

/**
 * The finalize workflow of one finalized billing report: each step in the order
 * the workflow runs them, that step's outcome, and every attempt at it with the
 * user who made it (R28).
 *
 * THE STEPS ARE THE API'S, IN THE API'S ORDER. Nothing here knows there are two
 * of them, names either, or pairs them off — a later phase adds a third step,
 * reports sent to clients, and it must appear here without this file changing
 * (spec §The finalize workflow).
 *
 * There is no advance control, because steps advance on their own once the one
 * before them has succeeded (R11). The only control is Re-run, and there is one
 * of it rather than one per step (R28a).
 */
const BillingFinalizeWorkflow = ({ reportId }: { reportId: number }) => {
  const url = `/v1/billing/reports/${reportId}/finalize-workflow`;
  const queryClient = useQueryClient();

  const {
    isPending,
    isError,
    data: workflowBody,
  } = useAuthenticatedFetch<WorkflowBody>(
    url,
    {},
    {
      refetchInterval: (query) =>
        isWithTheWorker(query.state.data) ? REFETCH_INTERVAL_MS : false,
    },
  );

  /**
   * The firm's users, for the names the attempt lines carry (APP-1474).
   *
   * Read WITHOUT blocking the panel on it: this query's pending, error and
   * empty states are all the same state as far as the panel is concerned — the
   * map has no entry for the subject, and the attempt line shows the subject.
   * The workflow is what the advisor came for, and a users read that is slow or
   * failing must not hold it back or take it away.
   *
   * `/users` is the key `UsersManager` reads under too, so a firm page already
   * visited this session serves this from cache.
   */
  const { data: usersBody } = useAuthenticatedFetch<UsersBody>("/users");

  const names = useMemo<UserNames>(() => {
    const byId = new Map<string, string>();

    for (const user of usersBody?.data ?? []) {
      // Auth0 fills `name` with the email where the user set no name, so the
      // fallback is for a payload that omits it rather than for a nameless user.
      byId.set(user.user_id, user.name ?? user.email);
    }

    return byId;
  }, [usersBody]);

  const rerun = useAuthenticatedMutationNew<
    UnpackResponse<BillingController["rerunBillingReportFinalizeWorkflow"]>,
    void,
    void
  >({
    mutationKey: [
      "POST",
      "v1",
      "billing",
      "reports",
      reportId,
      "finalize-workflow",
      "attempts",
    ],
    mutationFn: () => undefined,
  });

  const { mutate } = rerun;

  const onRerun = useCallback(() => {
    mutate(undefined, {
      // The re-run starts an attempt in the worker; the panel learns what it
      // did by re-reading, the same way it learns everything else it shows.
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: [url] });
      },
    });
  }, [mutate, queryClient, url]);

  const stepToRerun = workflowBody?.data.stepToRerun ?? null;

  /**
   * Whether to offer the re-run control.
   *
   * `stepToRerun === null` is the api saying every step succeeded, and R29 is
   * that the control is then not offered at all: re-running would restart no
   * step and issue no Slant request, leaving the advisor unable to tell success
   * from a silent failure (AC15). Which step a re-run restarts is the api's
   * judgement too — this asks, it does not work it out again.
   *
   * A `running` attempt is the case this reads more closely than the api does.
   * Offering Re-run beside a step that is genuinely working invites the advisor
   * to restart it — a publish is mid-flight for over an hour — but withholding
   * it whenever the row says `running` is worse, because the re-run route is the
   * only thing that can resolve a `running` row: a worker killed mid-step would
   * leave the report with no control that could ever move it.
   *
   * The runner stamps `updated_time` every
   * `BILLING_FINALIZE_HEARTBEAT_MS` while a step runs, so the two cases are
   * distinguishable and the control is offered only for an attempt whose process
   * has stopped saying it is there.
   */
  const rerunnableStep =
    stepToRerun === null
      ? undefined
      : workflowBody?.data.steps.find((step) => step.step === stepToRerun);

  const latestAttempt = rerunnableStep?.attempts.at(-1);

  const heartbeatHasStopped =
    typeof latestAttempt !== "undefined" &&
    Date.now() - deserializeDate(latestAttempt.updatedTime).getTime() >
      RUNNING_ATTEMPT_STALE_AFTER_MS;

  const showRerun =
    typeof rerunnableStep !== "undefined" &&
    (rerunnableStep.outcome !== "running" || heartbeatHasStopped);

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mt-4">
        <h2 className="mb-0">Finalize Workflow</h2>
        {!showRerun ? null : (
          <SubmitButton
            label="Re-run"
            variant="secondary"
            onClick={onRerun}
            isSubmitting={rerun.isPending}
          />
        )}
      </div>
      {!rerun.isError ? null : (
        <Alert variant="danger" className="mt-2">
          The step could not be re-run.
        </Alert>
      )}
      {isPending ? (
        <Loading />
      ) : isError ? (
        <FormError message="Failed to load the finalize workflow" />
      ) : (
        <ListGroup as="ol" className="mt-2 mb-4">
          {workflowBody.data.steps.map((step) => (
            <WorkflowStep key={step.step} step={step} names={names} />
          ))}
        </ListGroup>
      )}
    </>
  );
};

export default BillingFinalizeWorkflow;
